import type { DateFilters, DayRange, SetDayRangeValue } from '@/hooks/useDateRangeFilter'
import { CalendarFilled } from '@ant-design/icons'
import { DefaultSlider } from '@components/_SiteWide/Grid/ActionButtons/DateRangeFilter/Slider'
import { GraviButton } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Dropdown } from 'antd'
import classNames from 'classnames'
import { endOfDay, min as minFn } from 'date-fns'
import moment from 'moment'
import type React from 'react'
import { useMemo, useState } from 'react'
import { DateRange, DateRangePicker } from 'react-date-range'

interface DateRangeFilterProps {
  setCurrentFilters: (filters: DateFilters) => void
  currentFilters: DateFilters
  fromKey?: string
  toKey?: string
  getFromStorage: boolean
  dayRangeStorage: {}
  setDayRangeStorage: ({}) => void
  min?: number
  max?: number
  dateRangeVariation?: boolean
  maxDate?: Date
  minDate?: Date
  maxDateRange?: number
  defaultSliderValues?: [number, number]
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  setCurrentFilters,
  currentFilters,
  fromKey = 'From',
  toKey = 'To',
  getFromStorage,
  dayRangeStorage,
  setDayRangeStorage,
  min,
  max,
  dateRangeVariation = false,
  maxDate,
  minDate,
  maxDateRange, // The maximum number of days that can be selected
  defaultSliderValues,
}) => {
  const [visible, setVisible] = useState(false)
  const isSet = useMemo(() => !!currentFilters?.[fromKey], [currentFilters?.[fromKey]])
  const _maxDate = max !== undefined && max !== null ? moment().add(max, 'days').toDate() : maxDate
  const _minDate = min !== undefined && min !== null ? moment().add(min, 'days').toDate() : minDate

  const dateChange = (item) => {
    const newFilters = {
      ...currentFilters,
      [fromKey]: moment(item.selection.startDate),
      [toKey]: minFn([
        moment(item.selection.startDate)
          .add(maxDateRange - 1, 'days')
          .toDate(),
        moment(endOfDay(item.selection.endDate)).toDate(),
      ]),
    }

    setCurrentFilters(newFilters)
    setDayRangeStorage({
      [fromKey]: moment(item.selection.startDate),
      [toKey]: moment(endOfDay(item.selection.endDate)),
    })
  }

  const dateFilters = [
    {
      startDate: currentFilters?.[fromKey] && new Date(currentFilters[fromKey]),
      endDate: currentFilters?.[toKey] && new Date(currentFilters[toKey]),
      key: 'selection',
    },
  ]
  const commonProps = {
    onChange: (item) => dateChange(item),
    showSelectionPreview: true,
    moveRangeOnFirstSelection: false,
    months: 2,
    ranges: dateFilters,
    direction: 'horizontal',
    format: formatters.DATE,
    minDate: _minDate,
    maxDate: _maxDate,
  }

  const menu = (
    <>
      {getFromStorage && (
        <DefaultSlider
          fromKey={fromKey}
          toKey={toKey}
          setCurrentFilters={setCurrentFilters}
          dayRangeStorage={dayRangeStorage}
          setDayRangeStorage={setDayRangeStorage}
          min={min}
          max={max}
          maxDateRange={maxDateRange}
          defaultSliderValues={defaultSliderValues}
        />
      )}

      {dateRangeVariation ? <DateRange {...commonProps} /> : <DateRangePicker {...commonProps} />}
    </>
  )

  return (
    <div>
      <Dropdown overlay={menu} onOpenChange={(flag) => setVisible(flag)} open={visible} placement='bottomRight'>
        <GraviButton
          icon={<CalendarFilled />}
          size='large'
          className={classNames({ 'not-set': !isSet })}
          onClick={(e) => e.preventDefault()}
          theme2={isSet}
        >
          {isSet
            ? `${dateFilters[0]?.startDate?.toLocaleDateString()} - ${dateFilters[0]?.endDate?.toLocaleDateString()}`
            : 'Filter By Date'}
        </GraviButton>
      </Dropdown>
    </div>
  )
}
